import { useMatchAllTermsContext } from "@/features/match-all-terms";
import { LOCATION_EXACTNESS_SCOPE } from "@/entities/place";
import type { FormAPI, PrefixObjectAccessor } from "@/shared/forms";
import { useFormAPI } from "@/shared/forms";
import type { FormValueEventInstance, FormValues } from "./types";
import { EVENT_DEFAULT_INSTANCE } from "./utils";

type EventName = keyof FormValues["events"];
type FormPath = PrefixObjectAccessor<FormValues, []>;

interface UseEventInstanceOptions {
    eventName: EventName;
}

/**
 * FormAPI.setFieldValue uses a generic that infers the value type from the path literal.
 * With computed paths (string variables), TypeScript can't resolve the path→value mapping.
 * This overloaded helper provides explicit path→value type contracts for our known paths.
 */
type SetField = {
    (path: FormPath, value: FormValueEventInstance): void;
    (path: FormPath, value: FormValueEventInstance[]): void;
    (path: FormPath, value: number | null): void;
};

/**
 * Manages the first instance of a "special" event (Self, SelfBirth) owned by a leaf field.
 *
 * Domain rule: an instance with no user-entered data should not exist. When the last
 * user-entered field is cleared, the instance is removed so downstream consumers don't
 * see a stale empty record.
 *
 * Used by Place and BirthYearField — the two leaf fields that own a special event's
 * first instance.
 */
export const useEventInstance = ({ eventName }: UseEventInstanceOptions) => {
    const { getFieldValue, setFieldValue } = useFormAPI() as unknown as FormAPI<FormValues>;
    const { matchAllTermsSupported, matchAllTermsExactly } = useMatchAllTermsContext();
    const instancePath = `events.${eventName}.instances.0` as FormPath;
    const instancesPath = `events.${eventName}.instances` as FormPath;
    const dateFieldPath = `${instancePath}.date.year` as FormPath;
    const dateProximityPath = `${instancePath}.dateProximity.year` as FormPath;
    const locationExactnessPath = `${instancePath}.locationExactness` as FormPath;

    const setField = setFieldValue as SetField;
    const getInstance = () => getFieldValue(instancePath) as unknown as FormValueEventInstance | undefined;

    /** Returns a fresh instance, applying match-all-terms exactness defaults if active. */
    const buildNewInstance = (): FormValueEventInstance => {
        const instance = structuredClone(EVENT_DEFAULT_INSTANCE);
        if (matchAllTermsSupported && matchAllTermsExactly) {
            instance.dateProximity.year = 0;
            instance.locationExactness = LOCATION_EXACTNESS_SCOPE.ThisPlace;
        }
        return instance;
    };

    /** True when the instance carries no user-entered data (location/gpid/date). */
    const isEmpty = (i: FormValueEventInstance): boolean =>
        !i.location && !i.gpid && !i.date.year && !i.date.month && !i.date.day;

    return {
        instancePath,
        dateFieldPath,
        dateProximityPath,
        locationExactnessPath,

        setLocation: ({ location, gpid }: { location: string; gpid: string }) => {
            const instance = getInstance() ?? buildNewInstance();
            setField(instancePath, { ...instance, location, gpid });
        },

        clearLocation: () => {
            const instance = getInstance();
            if (!instance) return;
            const next = { ...instance, location: "", gpid: "" };
            if (isEmpty(next)) setField(instancesPath, []);
            else setField(instancePath, next);
        },

        setYear: (year: number) => {
            const instance = getInstance();
            if (instance) {
                // Narrow leaf-path write to avoid disturbing other fields' subscriptions.
                setField(dateFieldPath, year);
            } else {
                const newInstance = buildNewInstance();
                newInstance.date.year = year;
                setField(instancePath, newInstance);
            }
        },

        clearYear: () => {
            const instance = getInstance();
            if (!instance) return;
            const next = { ...instance, date: { ...instance.date, year: null } };
            if (isEmpty(next)) setField(instancesPath, []);
            else setField(dateFieldPath, null);
        },
    };
};
